import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, Globe, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll get back to you soon.");
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success('Successfully subscribed to newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    }
  };

  return (
    <div className="contact-page" style={{ color: '#e2e8f0' }}>
      {/* Hero Section */}
      <section className="hero-section" style={{
        backgroundImage: 'linear-gradient(135deg, rgba(10, 126, 164, 0.7) 0%, rgba(6, 90, 117, 0.7) 100%)',
        minHeight: '50vh'
      }}>
        <div className="hero-content">
          <div className="hero-badge">
            <Mail className="w-4 h-4" />
            <span>Get In Touch</span>
          </div>
          <h1 className="hero-title">Let's Transform Together</h1>
          <p className="hero-subtitle">
            Ready to transform your business? Get in touch with our experts
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" style={{ color: '#e2e8f0' }}>
        <div className="container" style={{ color: '#e2e8f0' }}>
          <div className="contact-grid">
            <Card className="contact-form-card" style={{ color: '#e2e8f0' }}>
              <CardHeader>
                <CardTitle style={{ color: '#e2e8f0' }}>Send us a message</CardTitle>
                <CardDescription style={{ color: '#94a3b8' }}>Fill out the form and we'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name" style={{ color: '#e2e8f0' }}>Name *</label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        style={{ color: '#e2e8f0' }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email" style={{ color: '#e2e8f0' }}>Email *</label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={{ color: '#e2e8f0' }}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company" style={{ color: '#e2e8f0' }}>Company</label>
                      <Input
                        id="company"
                        name="company"
                        placeholder="Company Name"
                        value={formData.company}
                        onChange={handleInputChange}
                        style={{ color: '#e2e8f0' }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone" style={{ color: '#e2e8f0' }}>Phone</label>
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="+1 234 567 8900"
                        value={formData.phone}
                        onChange={handleInputChange}
                        style={{ color: '#e2e8f0' }}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="service" style={{ color: '#e2e8f0' }}>Service Interest</label>
                    <Input
                      id="service"
                      name="service"
                      placeholder="e.g., Legacy Modernization, Healthcare Solutions, Travel CRM"
                      value={formData.service}
                      onChange={handleInputChange}
                      style={{ color: '#e2e8f0' }}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" style={{ color: '#e2e8f0' }}>Message *</label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your needs..."
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      style={{ color: '#e2e8f0' }}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="submit-btn" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="contact-info">
              <Card className="contact-info-card" style={{ color: '#e2e8f0' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#e2e8f0' }}>Contact Information</CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>Reach out to us through any of these channels</CardDescription>
                </CardHeader>
                <CardContent className="contact-info-content">
                  <div className="contact-item">
                    <Mail className="w-5 h-5" style={{ color: '#22d3ee' }} />
                    <div>
                      <div className="contact-label" style={{ color: '#94a3b8' }}>Email</div>
                      <a href="mailto:suman.chimata@primefields.in" className="contact-value" style={{ color: '#e2e8f0' }}>suman.chimata@primefields.in</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <Phone className="w-5 h-5" style={{ color: '#22d3ee' }} />
                    <div>
                      <div className="contact-label" style={{ color: '#94a3b8' }}>Phone</div>
                      <a href="tel:+919876543210" className="contact-value" style={{ color: '#e2e8f0' }}>+91 98765 43210</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <Globe className="w-5 h-5" style={{ color: '#22d3ee' }} />
                    <div>
                      <div className="contact-label" style={{ color: '#94a3b8' }}>Website</div>
                      <a href="https://www.primefields.in" target="_blank" rel="noopener noreferrer" className="contact-value" style={{ color: '#e2e8f0' }}>www.primefields.in</a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="newsletter-card" style={{ color: '#e2e8f0' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#e2e8f0' }}>Stay Updated</CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>Subscribe to our newsletter for industry insights and updates</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      style={{ color: '#e2e8f0' }}
                    />
                    <Button type="submit" className="newsletter-btn">
                      Subscribe
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card className="business-hours-card" style={{ marginTop: '1.5rem', color: '#e2e8f0' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#e2e8f0' }}>Business Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Monday - Friday</span>
                      <span style={{ color: '#e2e8f0', fontWeight: 500 }}>9:00 AM - 6:00 PM</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Saturday</span>
                      <span style={{ color: '#e2e8f0', fontWeight: 500 }}>10:00 AM - 4:00 PM</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Sunday</span>
                      <span style={{ color: '#e2e8f0', fontWeight: 500 }}>Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
